import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { makeRequest } from "../../utils/request";
import { PageLayout } from "../../components/PageLayout";
import { Alert, AlertDescription, Badge, Button, Dialog, DropdownField, Icon, InputField, Spinner, SwitchField, Text, View } from "../../components/base";
import { RangePicker, rangeToDates, Section, useAdminFetch } from "../../components/admin";
import type { RangeKey } from "../../components/admin";
import { formatDateShort } from "../../utils/adminFormat";
import type { OrgActivityDetail } from "../../types/admin";
import {
  AddFundsDialog,
  AGNOSTIC_PRICING_KEYS,
  BILLING_MODEL_OPTIONS,
  EMPTY_EDIT_FORM,
  FLAT_PRICING_KEYS,
  OrgDetailSections,
  PRICING_DEFAULTS,
  settingsToEditForm,
  TIER_OPTIONS,
  UNBUNDLED_PRICING_KEYS,
  type EditFormState,
  type OrganizationSettings,
  type PricingRateKey,
} from "./adminOrgShared";

/**
 * Dedicated view + management page for a single organization. The
 * organizations list keeps a read-only shelf of the same activity detail;
 * editing and deleting an org happen only here.
 */
type OrgTab = "details" | "manage" | "pricing";

/** Labels for every pricing rate field; the Pricing section renders only
 *  the selected strategy's fields plus the strategy-agnostic ones. */
const PRICING_FIELD_DEFS: Array<{ key: PricingRateKey; label: string; hint?: string }> = [
  // Flat strategy
  { key: "sessionCostPerMinute", label: "Session Cost ($/min)" },
  // Unbundled strategy
  { key: "audioRuntimeCostPerMinute", label: "Audio Runtime ($/min)" },
  { key: "agentRuntimeCostPerMinute", label: "Agent Runtime ($/min)" },
  { key: "mediaStreamsCostPerMinute", label: "Media Streams ($/min)" },
  { key: "telephonyOutboundCostPerMinute", label: "Telephony Outbound ($/min)" },
  { key: "telephonyInboundCostPerMinute", label: "Telephony Inbound ($/min)" },
  { key: "telephonySipOutboundCostPerMinute", label: "SIP Outbound ($/min)", hint: "Reserved — SIP not billed yet." },
  { key: "telephonySipInboundCostPerMinute", label: "SIP Inbound ($/min)", hint: "Reserved — SIP not billed yet." },
  { key: "sttUpchargeMultiplier", label: "STT Upcharge Multiplier", hint: "Provider cost × multiplier." },
  { key: "ttsUpchargeMultiplier", label: "TTS Upcharge Multiplier", hint: "Provider cost × multiplier." },
  // Billed in both strategies
  { key: "evalsTokenCostMultiplier", label: "Eval Token Multiplier" },
  { key: "phoneNumberMonthlyRate", label: "Phone # Rate ($/mo)" },
  { key: "completionsTokenCostMultiplier", label: "Completions Token Multiplier" },
];

export const AdminOrganizationDetailPage = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  // Active tab pinned in the URL (?tab=manage) so views are linkable and
  // survive refresh; the default tab keeps a clean URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab") as OrgTab | null;
  const activeTab: OrgTab = tabParam === "manage" || tabParam === "pricing" ? tabParam : "details";
  const setActiveTab = (id: OrgTab) => {
    const next = new URLSearchParams(searchParams);
    if (id === "details") next.delete("tab");
    else next.set("tab", id);
    setSearchParams(next, { replace: true });
    // Leaving a tab discards its unsaved edits — the save/cancel controls
    // live in the tab row and are scoped to the visible tab.
    revertSettings();
    revertPricing();
    setSettingsSaved(false);
    setPricingSaved(false);
  };
  const [range, setRange] = useState<RangeKey>("30d");
  const [isAddFundsOpen, setIsAddFundsOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);
  const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);
  const [settingsError, setSettingsError] = useState("");
  const [editForm, setEditForm] = useState<EditFormState>(EMPTY_EDIT_FORM);
  // Last-saved values — each section's dirty state (and therefore its Save
  // button) is scoped to its own fields.
  const [baseline, setBaseline] = useState<EditFormState>(EMPTY_EDIT_FORM);
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [settingsSaveError, setSettingsSaveError] = useState("");
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [isSavingPricing, setIsSavingPricing] = useState(false);
  const [pricingSaveError, setPricingSaveError] = useState("");
  const [pricingSaved, setPricingSaved] = useState(false);

  const query = useMemo(() => {
    const { startDate, endDate } = rangeToDates(range);
    const params = new URLSearchParams();
    if (startDate) params.set("startDate", startDate);
    if (endDate) params.set("endDate", endDate);
    return params.toString();
  }, [range]);

  const {
    data: detailResponse,
    isLoading: isLoadingDetail,
    error: detailError,
    refresh: refreshDetail,
  } = useAdminFetch<{ data: OrgActivityDetail }>(organizationId ? `/v1/admin/activity/organizations/${organizationId}?${query}` : null);

  const detail = detailResponse?.data ?? null;
  const orgName = detail?.organization.name ?? "Organization";
  const orgTier = detail?.organization.tier;
  const createdAt = detail?.organization.createdAt;
  const periodLabel = range === "all" ? "All time" : `Last ${range}`;

  // The settings form is a first-class section of this page, so it loads on
  // mount rather than behind an edit toggle.
  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;

    setIsLoadingSettings(true);
    setIsSettingsLoaded(false);
    setSettingsError("");
    setSettingsSaveError("");
    setPricingSaveError("");
    setSettingsSaved(false);
    setPricingSaved(false);

    (async () => {
      try {
        const { data } = await makeRequest<{ data: OrganizationSettings }>(`/v1/organizations/${organizationId}`);
        if (cancelled) return;
        const form = settingsToEditForm(data);
        setEditForm(form);
        setBaseline(form);
        setIsSettingsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        setSettingsError(err instanceof Error ? err.message : "Failed to load organization settings.");
      } finally {
        if (!cancelled) setIsLoadingSettings(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const SETTINGS_KEYS = ["tier", "outboundEnabled"] as const;
  // The strategy's own rates + the strategy-agnostic ones — exactly what the
  // form shows and what a save submits (the API rejects fields belonging to
  // the other strategy).
  const strategyKeys: readonly PricingRateKey[] =
    editForm.billingModel === "unbundled" ? UNBUNDLED_PRICING_KEYS : FLAT_PRICING_KEYS;
  const activePricingKeys: readonly PricingRateKey[] = [...strategyKeys, ...AGNOSTIC_PRICING_KEYS];
  const isSettingsDirty = SETTINGS_KEYS.some((key) => editForm[key] !== baseline[key]);
  const isPricingDirty =
    editForm.billingModel !== baseline.billingModel || activePricingKeys.some((key) => editForm[key] !== baseline[key]);

  const revertSettings = () => {
    setEditForm((form) => ({ ...form, tier: baseline.tier, outboundEnabled: baseline.outboundEnabled }));
    setSettingsSaveError("");
  };

  const revertPricing = () => {
    setEditForm((form) => {
      const next = { ...form, billingModel: baseline.billingModel };
      for (const key of [...FLAT_PRICING_KEYS, ...UNBUNDLED_PRICING_KEYS, ...AGNOSTIC_PRICING_KEYS]) {
        next[key] = baseline[key];
      }
      return next;
    });
    setPricingSaveError("");
  };

  const handleSaveSettings = async () => {
    if (!organizationId) return;

    setIsSavingSettings(true);
    setSettingsSaveError("");

    try {
      await makeRequest(`/v1/organizations/${organizationId}`, {
        method: "PATCH",
        body: { tier: editForm.tier, outboundEnabled: editForm.outboundEnabled },
      });
      setBaseline((prev) => ({ ...prev, tier: editForm.tier, outboundEnabled: editForm.outboundEnabled }));
      setSettingsSaved(true);
      refreshDetail();
    } catch (err) {
      setSettingsSaveError(err instanceof Error ? err.message : "Failed to save settings.");
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSavePricing = async () => {
    if (!organizationId) return;

    setIsSavingPricing(true);
    setPricingSaveError("");

    try {
      // The selected strategy's rates are required — the API enforces the
      // same, so fail here with a labeled message instead of a 400.
      const missing = PRICING_FIELD_DEFS.find((f) => strategyKeys.includes(f.key) && isNaN(parseFloat(editForm[f.key])));
      if (missing) {
        setPricingSaveError(`${missing.label} is required for the ${editForm.billingModel} pricing strategy.`);
        return;
      }

      // Submit only the selected strategy's rates (+ agnostic ones) — the
      // API 400s on fields that belong to the other strategy.
      const body: Record<string, unknown> = { billingModel: editForm.billingModel };
      for (const key of activePricingKeys) {
        const parsed = parseFloat(editForm[key]);
        if (!isNaN(parsed)) body[key] = parsed;
      }

      await makeRequest(`/v1/organizations/${organizationId}`, { method: "PATCH", body });
      setBaseline((prev) => {
        const next = { ...prev, billingModel: editForm.billingModel };
        for (const key of activePricingKeys) next[key] = editForm[key];
        return next;
      });
      // The hidden strategy's fields were not submitted; snap any stray
      // edits back to their last-saved values so the form matches the API.
      setEditForm((form) => {
        const next = { ...form };
        for (const key of [...FLAT_PRICING_KEYS, ...UNBUNDLED_PRICING_KEYS]) {
          if (!activePricingKeys.includes(key)) next[key] = baseline[key];
        }
        return next;
      });
      setPricingSaved(true);
      refreshDetail();
    } catch (err) {
      setPricingSaveError(err instanceof Error ? err.message : "Failed to save pricing.");
    } finally {
      setIsSavingPricing(false);
    }
  };

  const handleDelete = async () => {
    if (!organizationId) return;

    setIsDeleting(true);
    setDeleteError("");

    try {
      await makeRequest(`/v1/organizations/${organizationId}`, { method: "DELETE" });
      setIsDeleteOpen(false);
      navigate("/admin/organizations");
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : "Failed to delete organization.");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <PageLayout
      title={orgName}
      description={createdAt ? `Created ${formatDateShort(createdAt)} · ${organizationId}` : organizationId}
      backTo="/admin/organizations"
      backLabel="Organizations"
      tabs={[
        { id: "details", label: "Details" },
        { id: "manage", label: "Manage" },
        { id: "pricing", label: "Pricing" },
      ]}
      activeTab={activeTab}
      onTabChange={(id) => setActiveTab(id as OrgTab)}
      tabsBorder={false}
      tabAction={
        (activeTab === "manage" && (isSettingsDirty || isSavingSettings)) ||
        (activeTab === "pricing" && (isPricingDirty || isSavingPricing)) ? (
          <View className="flex flex-row items-center gap-2">
            <Button
              variant="success"
              size="sm"
              outline
              title="Save Changes"
              onClick={activeTab === "manage" ? handleSaveSettings : handleSavePricing}
              isDisabled={activeTab === "manage" ? isSavingSettings : isSavingPricing}
            >
              <Icon
                name={(activeTab === "manage" ? isSavingSettings : isSavingPricing) ? "loader" : "check"}
                size={14}
                className={(activeTab === "manage" ? isSavingSettings : isSavingPricing) ? "animate-spin" : undefined}
              />
            </Button>
            <Button
              variant="neutral"
              size="sm"
              outline
              title="Cancel"
              onClick={activeTab === "manage" ? revertSettings : revertPricing}
              isDisabled={activeTab === "manage" ? isSavingSettings : isSavingPricing}
            >
              <Icon
                name="close"
                size={14}
              />
            </Button>
          </View>
        ) : undefined
      }
      headerAction={orgTier === "enterprise" ? <Badge variant="info">Enterprise</Badge> : undefined}
    >
      <View className="mt-6 flex flex-col gap-2">
        {activeTab === "details" && (
          <>
            <View className="flex flex-row justify-end">
              <RangePicker
                value={range}
                onChange={setRange}
              />
            </View>

            {detailError && (
              <Alert variant="destructive">
                <AlertDescription>{detailError}</AlertDescription>
              </Alert>
            )}

            {isLoadingDetail && !detail ? (
              <View className="flex justify-center py-10">
                <Spinner size={24} />
              </View>
            ) : (
              detail && (
                <OrgDetailSections
                  detail={detail}
                  periodLabel={periodLabel}
                  tileColumns={4}
                />
              )
            )}
          </>
        )}

        {activeTab === "manage" && (
          <View className="flex max-w-xl flex-col gap-8">
            <Section title="Credits">
              <View className="flex flex-row">
                <Button
                  variant="info"
                  size="sm"
                  outline
                  onClick={() => setIsAddFundsOpen(true)}
                >
                  <Icon
                    name="payments"
                    size={14}
                  />
                  Add funds
                </Button>
              </View>
            </Section>

            {settingsError && (
              <Alert variant="destructive">
                <AlertDescription>{settingsError}</AlertDescription>
              </Alert>
            )}
            {isLoadingSettings ? (
              <View className="flex justify-center py-6">
                <Spinner size={20} />
              </View>
            ) : (
              isSettingsLoaded && (
                <Section title="Settings">
                  <View className="flex flex-col gap-4">
                    <View className="grid grid-cols-1 gap-4">
                      <DropdownField
                        label="Tier"
                        options={TIER_OPTIONS}
                        value={editForm.tier}
                        onChange={(value) => setEditForm((form) => ({ ...form, tier: value }))}
                      />
                      <SwitchField
                        label="Outbound Enabled"
                        value={editForm.outboundEnabled ? "true" : "false"}
                        onChange={(value) => setEditForm((form) => ({ ...form, outboundEnabled: value === "true" }))}
                        options={[
                          { label: "Disabled", value: "false" },
                          { label: "Enabled", value: "true" },
                        ]}
                      />
                    </View>
                    {settingsSaveError && <Text className="text-sm text-red-500">{settingsSaveError}</Text>}
                    {settingsSaved && !isSettingsDirty && !isSavingSettings && (
                      <Text className="text-xs text-gray-400 dark:text-gray-500">Saved</Text>
                    )}
                  </View>
                </Section>
              )
            )}

            <Section title="Danger zone">
              <View className="flex flex-row">
                <Button
                  variant="danger"
                  size="sm"
                  outline
                  onClick={() => setIsDeleteOpen(true)}
                >
                  <Icon
                    name="delete"
                    size={14}
                  />
                  Delete organization
                </Button>
              </View>
            </Section>
          </View>
        )}

        {activeTab === "pricing" && (
          <>
            {settingsError && (
              <Alert variant="destructive">
                <AlertDescription>{settingsError}</AlertDescription>
              </Alert>
            )}
            {isLoadingSettings ? (
              <View className="flex justify-center py-10">
                <Spinner size={24} />
              </View>
            ) : (
              isSettingsLoaded && (
                <View className="flex flex-col gap-8">
                  <View className="grid grid-cols-1 gap-8 lg:grid-cols-2">
                    <Section title="General">
                      <View className="grid grid-cols-1 gap-4">
                        {PRICING_FIELD_DEFS.filter((f) => AGNOSTIC_PRICING_KEYS.includes(f.key as (typeof AGNOSTIC_PRICING_KEYS)[number])).map((f) => (
                          <InputField
                            key={f.key}
                            label={f.label}
                            type="number"
                            value={editForm[f.key]}
                            onChange={(value) => setEditForm((form) => ({ ...form, [f.key]: value }))}
                          />
                        ))}
                      </View>
                    </Section>

                    <Section title="Pricing Strategy">
                      <View className="flex flex-col gap-4">
                        <DropdownField
                          label="Pricing Strategy"
                          options={BILLING_MODEL_OPTIONS}
                          value={editForm.billingModel}
                          onChange={(value) =>
                            setEditForm((form) => {
                              // Prefill any empty field of the incoming
                              // strategy with the platform default, so the
                              // admin sees exactly what a save will apply.
                              const next = { ...form, billingModel: value };
                              const incoming = value === "unbundled" ? UNBUNDLED_PRICING_KEYS : FLAT_PRICING_KEYS;
                              for (const key of incoming) {
                                if (next[key].trim() === "") next[key] = PRICING_DEFAULTS[key];
                              }
                              return next;
                            })
                          }
                        />
                        <View className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          {PRICING_FIELD_DEFS.filter((f) => strategyKeys.includes(f.key)).map((f) => (
                            <View
                              key={f.key}
                              className="flex flex-col gap-1"
                            >
                              <InputField
                                label={f.label}
                                type="number"
                                value={editForm[f.key]}
                                onChange={(value) => setEditForm((form) => ({ ...form, [f.key]: value }))}
                              />
                              {f.hint && <Text className="text-[11px] text-gray-400 dark:text-gray-500">{f.hint}</Text>}
                            </View>
                          ))}
                        </View>
                      </View>
                    </Section>
                  </View>

                  {pricingSaveError && <Text className="text-sm text-red-500">{pricingSaveError}</Text>}
                  {pricingSaved && !isPricingDirty && !isSavingPricing && (
                    <Text className="text-xs text-gray-400 dark:text-gray-500">Saved</Text>
                  )}
                </View>
              )
            )}
          </>
        )}
      </View>

      <AddFundsDialog
        isOpen={isAddFundsOpen}
        organizationId={organizationId ?? null}
        organizationName={orgName}
        onClose={() => setIsAddFundsOpen(false)}
      />

      <Dialog
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteError("");
        }}
        title="Delete Organization"
        size="sm"
        actions={
          <>
            <Button
              variant="neutral"
              size="sm"
              outline
              onClick={() => {
                setIsDeleteOpen(false);
                setDeleteError("");
              }}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              size="sm"
              outline
              onClick={handleDelete}
              isDisabled={isDeleting}
            >
              {isDeleting ? (
                <Icon
                  name="loader"
                  size={14}
                  className="animate-spin"
                />
              ) : (
                <Icon
                  name="trash"
                  size={14}
                />
              )}
              Delete
            </Button>
          </>
        }
      >
        <View className="p-4 flex flex-col gap-3">
          <Text className="text-sm text-gray-900 dark:text-gray-100">Delete {orgName}? This cannot be undone.</Text>
          {deleteError && <Text className="text-sm text-red-500">{deleteError}</Text>}
        </View>
      </Dialog>
    </PageLayout>
  );
};
